import type { QuotePricingResponse } from "./insideSalesQuotesApi";

export interface QuoteProductPricingTier {
  minimumQuantity: string | null;
  maximumQuantity: string | null;
  price: string;
  pricingType: string;
}

export const QUOTE_CLIENT_TYPES = [
  { label: "List Price", value: "list" },
  { label: "Non-Stocking Distributor/End User Direct 5k+", value: "standard" },
  { label: "Stocking Distributor", value: "stocking" },
  { label: "Strategic Partner", value: "partner" },
] as const;

function optionalString(value: unknown): string | null {
  if (value == null || String(value).trim() === "") return null;
  return String(value);
}

function canonicalClientType(value: unknown): string {
  const normalized = optionalString(value)?.trim().toLowerCase() ?? "";
  if (normalized === "list" || normalized.includes("list price")) return "list";
  if (
    normalized === "standard"
    || normalized.includes("non-stocking")
    || normalized.includes("end user")
  ) return "standard";
  if (normalized === "stocking" || normalized.includes("stocking distributor")) return "stocking";
  if (normalized === "partner" || normalized.includes("strategic partner")) return "partner";
  return normalized;
}

function rawPricingRows(payload: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(payload)) {
    return payload.filter((row): row is Record<string, unknown> => row != null && typeof row === "object");
  }
  if (payload == null || typeof payload !== "object") return [];
  const record = payload as Record<string, unknown>;
  const nested = Array.isArray(record.pricing)
    ? record.pricing
    : Array.isArray(record.prices)
      ? record.prices
      : [];
  return nested.filter((row): row is Record<string, unknown> => row != null && typeof row === "object");
}

export function quoteProductPricingTiers(
  pricing: QuotePricingResponse,
  options: { includeSyntheticOpenEnded?: boolean } = {},
): QuoteProductPricingTier[] {
  const mapped = rawPricingRows(pricing.pricing)
    .map((tier) => ({
      minimumQuantity: optionalString(tier.min),
      maximumQuantity: optionalString(tier.max),
      price: optionalString(tier.price) ?? "",
      pricingType: canonicalClientType(tier.type),
    }))
    .filter((tier) => tier.price !== "");

  const available = mapped.length > 0
    ? mapped
    : pricing.unitPrice == null
      ? []
      : [{
          minimumQuantity: pricing.minimumQuantity,
          maximumQuantity: pricing.maximumQuantity,
          price: pricing.unitPrice,
          pricingType: canonicalClientType(pricing.pricingType),
        }];

  const includeSyntheticOpenEnded = options.includeSyntheticOpenEnded !== false;
  const withOpenRanges = [...available];
  if (!includeSyntheticOpenEnded) {
    return withOpenRanges;
  }
  const pricingTypes = [...new Set(available.map((tier) => tier.pricingType))];
  for (const pricingType of pricingTypes) {
    const typeTiers = available.filter((tier) => tier.pricingType === pricingType);
    if (typeTiers.some((tier) => tier.maximumQuantity == null)) continue;
    const maximum = Math.max(
      ...typeTiers.map((tier) => Number(tier.maximumQuantity)).filter(Number.isFinite),
    );
    if (!Number.isFinite(maximum)) continue;
    withOpenRanges.push({
      minimumQuantity: String(maximum + 1),
      maximumQuantity: null,
      price: "",
      pricingType,
    });
  }
  return withOpenRanges;
}

export function quoteProductTierRange(tier: QuoteProductPricingTier): string {
  if (tier.minimumQuantity && tier.maximumQuantity) {
    return `${tier.minimumQuantity} – ${tier.maximumQuantity}`;
  }
  if (tier.minimumQuantity) return `${tier.minimumQuantity} +`;
  if (tier.maximumQuantity) return `Up to ${tier.maximumQuantity}`;
  return "Any quantity";
}
